import { Event } from '@livecontrol/scheduler/model';
import type { ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { Tag, Tooltip } from '../../components';
import { Store } from '../../store';
import type { ScheduledEvent } from '../event-details';
import { CopyPill } from './copy-pill';
import { ReplaceAssetModal } from './replace-asset-modal';
import { buildWebplayerUrl } from './webplayer-url';

interface Props {
  scheduledEvent: ScheduledEvent;
  /** Refreshes the event and remounts the player after the asset is replaced. */
  onAssetReplaced: () => void;
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Separator = styled.div`
  width: 1px;
  height: 17px;
  background: rgba(255, 255, 255, 0.12);
  margin: 0px 3px;
  flex-shrink: 0;
`;

const Tags = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const IconButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(213, 221, 238, 0.92);
  font-size: 13px;
  flex-shrink: 0;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: var(--white);
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(17, 27, 51, 0.9);
  }

  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }
`;

const encoderTooltipText = (available: boolean, connected: boolean): string => {
  if (!available) {
    return 'Encoder Connect — no remote session for this event.';
  }

  return connected
    ? 'Encoder Connect — Splashtop Connected'
    : 'Encoder Connect — Connect to Splashtop';
};

export const PlayerBar = ({ scheduledEvent, onAssetReplaced }: Props): ReactElement => {
  const [replacing, setReplacing] = useState(false);
  const { me } = Store.User.useIdentity();

  const cid = scheduledEvent.location?.user?.id;
  const secondary = scheduledEvent.location?.secondary_id;
  const cidLabel = cid ? `${cid}${secondary ? `.${secondary}` : ''}` : '';
  const eid = scheduledEvent.id;

  const splashtopUuid = scheduledEvent.location?.splashtop_uuid;
  const encoderAvailable = Boolean(splashtopUuid);

  // TODO: wire the real remote-session status once a "connected" signal is available.
  const encoderConnected = false;

  const encoderHref = splashtopUuid
    ? `st-business://com.splashtop.business?account=${
        me?.splashtop_email ?? me?.email ?? ''
      }&uuid=${splashtopUuid}&sessiontype=remote`
    : undefined;

  const encoderTooltip = encoderTooltipText(encoderAvailable, encoderConnected);

  const webplayerUrl = buildWebplayerUrl(scheduledEvent);

  /*
   * Replacing only makes sense once the event is over and has a recording, and
   * the upload is scoped to a client. Same three conditions the old Command
   * Center used to show this button.
   */
  const assetId = scheduledEvent.assets?.id;
  const clientId = scheduledEvent.location?.user?.id;

  const canReplace =
    scheduledEvent.status === Event.Phase.Past.toString() &&
    assetId !== undefined &&
    clientId !== undefined;

  return (
    <Bar>
      <CopyPill label='CID' value={cidLabel} />

      <CopyPill label='EID' value={String(eid)} />

      <Separator />

      <Tags>
        {scheduledEvent.is_first_event && (
          <Tag size='small' className='cc-amber'>
            First Event
          </Tag>
        )}
        {scheduledEvent.receive_iso_recordings && (
          <Tag size='small' className='cc-neutral'>
            ISO
          </Tag>
        )}
        {Boolean(scheduledEvent.audio_cue_sheet_url) && (
          <Tag size='small' className='cc-neutral'>
            Audio Mix
          </Tag>
        )}
        {Boolean(scheduledEvent.operator_name) && (
          <Tag size='small' className='cc-neutral'>
            DVG
          </Tag>
        )}
      </Tags>

      <Tooltip content={encoderTooltip}>
        <IconButton
          className={encoderAvailable ? '' : 'disabled'}
          href={encoderHref}
          target='_blank'
          rel='noreferrer'
        >
          <i className='far fa-desktop' />
        </IconButton>
      </Tooltip>

      <Tooltip content={scheduledEvent.hidden ? 'Private event: webplayer link is hidden' : 'Webplayer'}>
        <IconButton
          className={webplayerUrl ? '' : 'disabled'}
          href={webplayerUrl || undefined}
          target='_blank'
          rel='noreferrer'
        >
          <i className={scheduledEvent.hidden ? 'far fa-eye-slash' : 'fal fa-play-circle'} />
        </IconButton>
      </Tooltip>

      {canReplace && (
        <>
          <Tooltip content='Replace asset'>
            <IconButton
              as='button'
              type='button'
              onClick={(): void => {
                setReplacing(true);
              }}
            >
              <i className='fal fa-cloud-upload' />
            </IconButton>
          </Tooltip>

          <ReplaceAssetModal
            open={replacing}
            assetId={assetId}
            clientId={String(clientId)}
            onReplaced={onAssetReplaced}
            onClose={(): void => {
              setReplacing(false);
            }}
          />
        </>
      )}
    </Bar>
  );
};
